<template>
  <div class="greyed-bg h-100 w-100 align-center d-flex">
    <v-container v-if="loading" class="pa-0" style="width: 540px">
      <v-skeleton-loader
        type="card"
        class="invitation-loader"
        width="100%"
      ></v-skeleton-loader>
    </v-container>
    <v-container v-else class="pa-0" style="width: 540px">
      <img
        src="@/assets/localInsight.png"
        alt="Local Insight"
        height="63"
        class="mb-6"
      />

      <v-card
        class="pa-0 text-left"
        elevation="3"
        style="border-top: solid 8px #3edbcf"
      >
        <div v-if="tokenValidationFailed">
          <v-card-text class="pt-5 text-h5 weight-600" style="color: #51627c">
            Oops! This invite link has expired
          </v-card-text>

          <v-card-text class="py-2">
            This can happen if the link has already been used or if too much
            time has passed since it was sent. Please request a new invite from
            the person who invited you, or log in if you already have an
            account.
          </v-card-text>

          <v-card-text>
            <v-btn
              color="primary"
              rounded="0"
              variant="elevated"
              block
              @click="$router.push('/login')"
            >
              Log in
            </v-btn>
          </v-card-text>
        </div>
        <div v-else>
          <v-form ref="form" v-model="formValid">
            <v-card-text class="pt-5 text-h5 weight-600" style="color: #51627c">
              Complete your account
            </v-card-text>

            <v-card-text class="py-0">
              <div class="text-body-2 font-weight-medium text">
                Please confirm your name
              </div>
              <v-text-field
                v-model="user.name"
                :rules="nameRules"
                label="Name"
                class="mt-3"
                variant="outlined"
                density="compact"
                rounded="0"
                autocomplete="off"
              />
            </v-card-text>

            <v-card-text class="py-0">
              <div class="text-body-2 font-weight-medium text">
                Set a strong password for your account
              </div>
              <div class="text-caption text-grey">
                Your password must be at least 8 characters. For added security
                consider using a mix of numbers, special characters and upper
                and lowercase letters
              </div>
              <v-text-field
                v-model="user.password"
                :rules="passwordRules"
                label="Password"
                :type="showPassword ? 'text' : 'password'"
                class="mt-3"
                variant="outlined"
                density="compact"
                rounded="0"
                @click:append-inner="showPassword = !showPassword"
                :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                autocomplete="off"
              />
            </v-card-text>

            <v-card-text class="pt-0 pb-0">
              <div class="text-body-2 font-weight-medium text">
                I have read and agree to OCSI’s:
              </div>
              <v-checkbox
                v-model="user.termsAccepted"
                :rules="termsRules"
                label="Terms of use"
                density="compact"
                color="primary"
                hide-details
              >
                <template #label="{ label }">
                  <a
                    class="text-primary ml-2"
                    href="https://localinsight.org/legal/terms-of-use/"
                    target="_blank"
                    @click.stop
                  >
                    {{ label }}
                  </a>
                </template>
              </v-checkbox>
              <v-checkbox
                v-model="user.privatePolicyAccepted"
                :rules="privacyRules"
                label="Privacy policy"
                density="compact"
                color="primary"
                class="mb-4"
                hide-details
              >
                <template #label="{ label }">
                  <a
                    class="text-primary ml-2"
                    href="https://localinsight.org/legal/privacy-policy/"
                    target="_blank"
                    @click.stop
                  >
                    {{ label }}
                  </a>
                </template>
              </v-checkbox>

              <div v-if="user.synced">
                <v-divider class="mb-6"></v-divider>
                <v-checkbox
                  v-model="user.emailNotifications"
                  label="Opt in for email notifications about Local Insight updates and training opportunities"
                  density="compact"
                  color="primary"
                  class="mb-4"
                  hide-details
                >
                  <template #label="{ label }">
                    <span class="text ml-2">{{ label }}</span>
                  </template>
                </v-checkbox>
              </div>
            </v-card-text>

            <v-card-text class="pb-5">
              <v-btn
                color="primary"
                rounded="0"
                type="submit"
                :disabled="!formValid"
                @click.prevent="submitForm"
                :loading="logginInSpinner"
                variant="elevated"
                block
              >
                continue
              </v-btn>
            </v-card-text>
          </v-form>
        </div>
      </v-card>
    </v-container>

    <OTP
      v-model="showOTPDialog"
      :email="user.email"
      @twofa-verified.once="login"
    />
  </div>
</template>
<script>
import { getCookie } from "../mixins/GetCookie";
import { setCookie } from "../mixins/SetCookie";
import OTP from "@/components/OTP.vue";

export default {
  name: "UserCreationInvite",
  components: { OTP },
  data() {
    return {
      user: {
        name: "",
        email: "",
        password: "",
        termsAccepted: false,
        privatePolicyAccepted: false,
        emailNotifications: false,
        synced: false,
      },
      showPassword: false,
      tokenValidationFailed: false,
      loading: true,
      logginInSpinner: false,
      formValid: false,
      nameRules: [
        (v) => !!v || "Name is required",
        (v) => (v && v.length <= 50) || "Name must be less than 50 characters",
      ],
      passwordRules: [
        (v) => !!v || "Password is required",
        (v) => v.length >= 8 || "Password must be at least 8 characters",
      ],
      termsRules: [(v) => !!v || "You must accept the terms and conditions"],
      privacyRules: [(v) => !!v || "You must accept the privacy policy"],
      showOTPDialog: false,
    };
  },
  mounted() {
    /* If user is already logged in then redirect to the main page. Otherwise
       validate the invite token to proceed with user creation
    */
    if (
      getCookie("BEARER_TOKEN") &&
      !this.$store.state.config.userProfile.is_public_site_user_profile
    ) {
      this.$router.push("/");
    } else {
      this.validateInvite();
    }
  },
  methods: {
    async validateInvite() {
      this.loading = true;
      const { email, invitationToken } = this.$route.query || {};

      if (!email || !invitationToken) {
        this.tokenValidationFailed = true;
        return (this.loading = false);
      }

      try {
        const { data } = await this.$axios.post("validate-invite", {
          email: email,
          token: invitationToken,
        });
        this.user.name = data.user.name;
        this.user.email = data.user.email;
        this.user.synced = data.user.synced;
      } catch {
        this.tokenValidationFailed = true;
      } finally {
        this.loading = false;
      }
    },
    login() {
      this.logginInSpinner = true;

      document.cookie =
        "OI_PUBLIC_SITE=; domain=.localinsight.org; expires=Thu, 01 Jan 1970 00:00:01 GMT;";

      // make the call!
      this.$axios
        .post("/login", {
          email: this.user.email,
          password: this.user.password,
        })
        .then((response) => {
          // set the bearer token cookie for 2 hours, add it to the axios auth header
          setCookie("BEARER_TOKEN", response.data.token, 2);
          this.$axios.defaults.headers.common["Authorization"] =
            "Bearer " + getCookie("BEARER_TOKEN");

          //reset router route to main page so referrer is not login page and next step works
          this.$router.push("/");

          this.emit.emit("getClientAndUserConfig", "map");
        })
        .catch((error) => {
          switch (error.response.status) {
            case 455: // 2FA required
              this.handle2FA(error.response.data.twofa_method);
              break;
            default: // all other errors
              this.emit.emit("systemMessage", {
                title: "Failed to login",
                message: error.response.data.message,
                timeout: 5000,
                colour: "error",
              });
              break;
          }
        })
        .finally(() => {
          setTimeout(() => {
            this.logginInSpinner = false;
          }, 5000);
        });
    },
    async submitForm() {
      this.$refs.form.validate();
      if (this.formValid) {
        this.logginInSpinner = true;
        try {
          await this.$axios.post("save-user-details", {
            token: this.$route.query.invitationToken,
            email: this.$route.query.email,
            name: this.user.name,
            password: this.user.password,
            emailNotifications: this.user.emailNotifications ?? false,
            termsAccepted: this.user.termsAccepted,
            privatePolicyAccepted: this.user.privatePolicyAccepted,
          });
          this.login();
        } catch (error) {
          this.logginInSpinner = false;
          this.emit.emit("systemMessage", {
            title: "Failed to create user",
            message: error.response.data.message,
            timeout: 5000,
            colour: "error",
          });
        }
      }
    },
    handle2FA() {
      this.showOTPDialog = true;
    },
  },
};
</script>
<style scoped></style>
<style>
.invitation-loader .v-skeleton-loader__bone .v-skeleton-loader__image {
  height: 400px;
}
</style>
